import React, { useEffect, useMemo, useState } from 'react';
import { X, Send, Building2, Plus, Check, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { ApplicationDetail, ApplicationStatus, ApplicationPayload, EmploymentType, Organization, OrganizationPayload, Resume } from '../../types';
import { applicationsService, organizationsService, getApiErrorMessage } from '../../services/api';
import { COLUMN_META, EMPLOYMENT_LABELS, resumeLabel } from './kanbanConstants';
import ResumeThumb from '../ResumeThumb';
import { formatDate } from '../../utils/formatDate';

interface Props {
  resumes: Resume[];
  initialResumeId?: number;
  initialOrganizationId?: number;
  initialStatus?: ApplicationStatus;
  application?: ApplicationDetail | null;
  onClose: () => void;
  onSaved: (detail: ApplicationDetail) => void;
}

const EMPLOYMENT_OPTIONS = Object.keys(EMPLOYMENT_LABELS) as EmploymentType[];

const todayDateString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const ApplicationFormModal: React.FC<Props> = ({
  resumes,
  initialResumeId,
  initialOrganizationId,
  initialStatus = 'APPLIED',
  application,
  onClose,
  onSaved,
}) => {
  const [resumeId, setResumeId] = useState<number | ''>(application?.resumeId ?? initialResumeId ?? '');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState<number | ''>(application?.organizationId ?? initialOrganizationId ?? '');
  const [jobTitle, setJobTitle] = useState(application?.jobTitle ?? '');
  const [jobUrl, setJobUrl] = useState(application?.jobUrl ?? '');
  const [location, setLocation] = useState(application?.location ?? '');
  const [employmentType, setEmploymentType] = useState<EmploymentType | ''>(application?.employmentType ?? '');
  const [appliedAt, setAppliedAt] = useState(application?.appliedAt ?? '');
  const [recruiterName, setRecruiterName] = useState(application?.recruiterName ?? '');
  const [recruiterEmail, setRecruiterEmail] = useState(application?.recruiterEmail ?? '');
  const [salaryRange, setSalaryRange] = useState(application?.salaryRange ?? '');
  const [notes, setNotes] = useState(application?.notes ?? '');
  const [status, setStatus] = useState<ApplicationStatus>(application?.status ?? initialStatus);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgWebsite, setNewOrgWebsite] = useState('');
  const [newOrgIndustry, setNewOrgIndustry] = useState('');
  const [newOrgLocation, setNewOrgLocation] = useState('');

  useEffect(() => {
    organizationsService
      .list()
      .then((res) => setOrganizations(res.data))
      .catch(() => setOrganizations([]));
  }, []);

  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickerOpen]);

  const selectedResume = useMemo(
    () => resumes.find((r) => r.id === resumeId) ?? null,
    [resumes, resumeId]
  );

  const selectedOrganizationName = useMemo(() => {
    const org = organizations.find((o) => o.id === organizationId);
    return org ? org.name : '';
  }, [organizations, organizationId]);

  const handleSave = async () => {
    if (!resumeId) {
      setError('Choose which resume this application is using.');
      return;
    }
    if (!jobTitle.trim()) {
      setError('Job title is required.');
      return;
    }
    if (appliedAt && appliedAt > todayDateString()) {
      setError('Applied on cannot be a future date.');
      return;
    }

    let finalOrgId = organizationId;
    if (showCreateOrg || (!finalOrgId && newOrgName.trim())) {
      if (!newOrgName.trim()) {
        setError('Please pick an existing company or enter a new company name.');
        return;
      }
      setIsSaving(true);
      setError('');
      try {
        const payload: OrganizationPayload = {
          name: newOrgName.trim(),
          website: newOrgWebsite.trim() || null,
          industry: newOrgIndustry.trim() || null,
          location: newOrgLocation.trim() || null,
        };
        const res = await organizationsService.create(payload);
        finalOrgId = res.data.id;
        setOrganizations((prev) => [...prev, res.data]);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Could not create the company.'));
        setIsSaving(false);
        return;
      }
    }

    if (!finalOrgId) {
      setError('Please pick a company.');
      return;
    }

    const payload: ApplicationPayload = {
      resumeId: Number(resumeId),
      organizationId: Number(finalOrgId),
      jobTitle: jobTitle.trim(),
      jobUrl: jobUrl.trim() || null,
      location: location.trim() || null,
      employmentType: employmentType || null,
      status,
      appliedAt: appliedAt || null,
      recruiterName: recruiterName.trim() || null,
      recruiterEmail: recruiterEmail.trim() || null,
      salaryRange: salaryRange.trim() || null,
      notes: notes.trim() || null,
    };

    setIsSaving(true);
    setError('');
    try {
      if (application) {
        const res = await applicationsService.update(application.id, payload);
        toast.success('Application updated.');
        onSaved(res.data);
      } else {
        const res = await applicationsService.create(payload);
        toast.success('Application added to your board.');
        onSaved(res.data);
      }
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
      setIsSaving(false);
    }
  };

  const inputClass =
    'w-full h-10 px-3 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-zinc-50 disabled:text-zinc-400';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-card shadow-elevated w-full max-w-xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="font-display font-semibold text-foreground">
            {application ? 'Edit application' : 'Add application'}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-foreground transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Resume used (required)</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPickerOpen((o) => !o)}
                  className={`w-full h-10 px-3 text-left text-sm bg-white border border-zinc-200 rounded-btn focus:outline-none focus:ring-2 focus:ring-brand-300 flex items-center justify-between gap-2`}
                >
                  <span className="truncate">
                    {selectedResume ? resumeLabel(selectedResume) : <span className="text-zinc-400">Select a resume…</span>}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
                </button>
                {pickerOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setPickerOpen(false)} />
                    <div className="absolute z-40 left-0 right-0 mt-2 max-h-72 overflow-y-auto rounded-card border border-zinc-200 bg-white shadow-elevated py-1.5">
                      {resumes.filter((r) => !r.disabled).length === 0 && (
                        <p className="px-3 py-2 text-xs text-zinc-400">No enabled resumes yet. Create one on the Dashboard first, or re-enable a disabled one.</p>
                      )}
                      {resumes.filter((r) => !r.disabled).map((resume) => (
                        <button
                          key={resume.id}
                          type="button"
                          onClick={() => {
                            setResumeId(resume.id);
                            setPickerOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50 transition-colors ${
                            resumeId === resume.id ? 'bg-brand-50' : ''
                          }`}
                        >
                          <div className="relative w-14 shrink-0 aspect-[1/1.414] rounded-btn border border-zinc-200 overflow-hidden bg-zinc-50">
                            <ResumeThumb resume={resume} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-medium text-foreground truncate">
                              {resume.personalInfo?.fullName || `${resume.templateId ?? 'resume'} #${resume.id}`}
                            </p>
                            <p className="text-[11px] text-zinc-400 truncate">
                              {resume.templateId}
                              {resume.createdAt ? ` · ${formatDate(resume.createdAt)}` : ''} · #{resume.id}
                            </p>
                          </div>
                          {resumeId === resume.id && <Check className="w-4 h-4 text-brand-500 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {selectedResume && (
                <div className="flex items-start gap-3 mt-2.5 rounded-card border border-zinc-200 p-2.5">
                  <div className="relative w-20 shrink-0 aspect-[1/1.414] rounded-btn border border-zinc-200 overflow-hidden bg-zinc-50">
                    <ResumeThumb resume={selectedResume} />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-[13px] font-semibold text-foreground truncate">
                      {selectedResume.personalInfo?.fullName}
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {selectedResume.templateId}
                      {selectedResume.createdAt ? ` · ${formatDate(selectedResume.createdAt)}` : ''}
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">#{selectedResume.id}</p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Application status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ApplicationStatus)}
                className={inputClass}
              >
                {(Object.keys(COLUMN_META) as ApplicationStatus[]).map((s) => (
                  <option key={s} value={s}>{COLUMN_META[s].label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Company (required)</label>
            {!showCreateOrg ? (
              <div className="flex gap-2">
                <select
                  value={organizationId}
                  onChange={(e) => setOrganizationId(Number(e.target.value) || '')}
                  className={inputClass}
                >
                  <option value="">Pick an existing company…</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setShowCreateOrg(true); setOrganizationId(''); }}
                  className="h-10 shrink-0 px-3 inline-flex items-center gap-1.5 border border-dashed border-zinc-300 hover:border-brand-400 hover:text-brand-600 text-zinc-500 text-xs font-medium rounded-btn transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New
                </button>
              </div>
            ) : (
              <div className="rounded-card border border-zinc-200 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-foreground inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-brand-500" /> New company
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateOrg(false);
                      setNewOrgName('');
                      setNewOrgWebsite('');
                      setNewOrgIndustry('');
                      setNewOrgLocation('');
                    }}
                    className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <input
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="Company name (required)"
                  className={inputClass}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    value={newOrgWebsite}
                    onChange={(e) => setNewOrgWebsite(e.target.value)}
                    placeholder="Website"
                    className={inputClass}
                  />
                  <input
                    value={newOrgIndustry}
                    onChange={(e) => setNewOrgIndustry(e.target.value)}
                    placeholder="Industry"
                    className={inputClass}
                  />
                </div>
                <input
                  value={newOrgLocation}
                  onChange={(e) => setNewOrgLocation(e.target.value)}
                  placeholder="Location"
                  className={inputClass}
                />
              </div>
            )}
            {organizationId && (
              <p className="text-xs text-zinc-400 mt-1.5 truncate">{selectedOrganizationName}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Job title (required)</label>
            <input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Senior Frontend Engineer"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Job posting URL</label>
              <input
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                placeholder="https://…"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Location</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Remote · Berlin, DE"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Employment type</label>
              <select
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value as EmploymentType | '')}
                className={inputClass}
              >
                <option value="">Not specified</option>
                {EMPLOYMENT_OPTIONS.map((type) => (
                  <option key={type} value={type}>{EMPLOYMENT_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Applied on</label>
              <input
                type="date"
                value={appliedAt}
                max={todayDateString()}
                onChange={(e) => setAppliedAt(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Recruiter name</label>
              <input
                value={recruiterName}
                onChange={(e) => setRecruiterName(e.target.value)}
                placeholder="Contact person"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1.5">Recruiter email</label>
              <input
                type="email"
                value={recruiterEmail}
                onChange={(e) => setRecruiterEmail(e.target.value)}
                placeholder="hr@company.com"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Salary range</label>
            <input
              value={salaryRange}
              onChange={(e) => setSalaryRange(e.target.value)}
              placeholder="e.g. ₹25–35 LPA or 90–110k €"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Deadlines, follow-ups, context…"
              className="w-full px-3 py-2 border border-zinc-300 rounded-btn text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-btn px-3 py-2">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-100">
          <button
            onClick={onClose}
            className="h-10 px-4 border border-zinc-200 hover:bg-zinc-50 text-zinc-600 text-sm font-medium rounded-btn transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="h-10 px-4 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {isSaving ? 'Saving…' : application ? 'Save changes' : 'Add to board'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationFormModal;